import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { inr, inrShort } from "@/lib/api";
import { Landmark } from "lucide-react";

const computeEmi = (p, annualRate, years) => {
  const n = years * 12;
  const r = annualRate / 12 / 100;
  const emi = r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const totalPayable = emi * n;
  return { emi, totalPayable, totalInterest: totalPayable - p, months: n };
};

const SliderRow = ({ label, value, display, min, max, step, onChange, testId }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <Label className="text-slate-200 font-semibold">{label}</Label>
      <span className="font-mono-data text-orange-400 font-bold" data-testid={`${testId}-value`}>
        {display}
      </span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
      data-testid={testId}
      className="[&_[role=slider]]:bg-orange-500 [&_[role=slider]]:border-orange-500 [&>span:first-child_span]:bg-orange-500"
    />
  </div>
);

export const EmiEstimator = ({ totalCost }) => {
  const suggested = Math.round((totalCost || 3000000) * 0.8);
  const [principal, setPrincipal] = useState(suggested);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  useEffect(() => {
    setPrincipal(Math.round((totalCost || 3000000) * 0.8));
  }, [totalCost]);

  const { emi, totalPayable, totalInterest } = useMemo(
    () => computeEmi(principal, rate, years),
    [principal, rate, years]
  );

  const donut = [
    { name: "Principal", value: principal, color: "#F97316" },
    { name: "Interest", value: Math.max(totalInterest, 0), color: "#3B82F6" },
  ];

  return (
    <Card className="bg-slate-900/70 border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="emi-estimator">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-emerald-500/15 grid place-items-center">
          <Landmark className="h-5 w-5 text-emerald-400" />
        </div>
        <div>
          <h3 className="font-heading text-xl font-bold text-white">Construction Loan / EMI</h3>
          <p className="text-sm text-slate-400">Plan your home construction finance</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="space-y-6">
          <SliderRow
            label="Loan Amount"
            value={principal}
            display={inrShort(principal)}
            min={100000}
            max={30000000}
            step={50000}
            onChange={setPrincipal}
            testId="emi-principal"
          />
          <SliderRow
            label="Interest Rate (p.a.)"
            value={rate}
            display={`${rate.toFixed(2)}%`}
            min={5}
            max={18}
            step={0.05}
            onChange={(v) => setRate(Number(v.toFixed(2)))}
            testId="emi-rate"
          />
          <SliderRow
            label="Tenure"
            value={years}
            display={`${years} yrs`}
            min={1}
            max={30}
            step={1}
            onChange={setYears}
            testId="emi-tenure"
          />
        </div>

        <div className="flex flex-col items-center">
          <div className="relative h-52 w-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donut} dataKey="value" innerRadius={62} outerRadius={85} paddingAngle={2} stroke="none">
                  {donut.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-content-center text-center">
              <span className="text-xs text-slate-400">Monthly EMI</span>
              <span className="font-heading text-2xl font-extrabold text-white font-mono-data" data-testid="emi-monthly">
                ₹{inr(emi)}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full mt-6">
            <div className="rounded-xl bg-slate-950 border border-slate-800 p-3 text-center">
              <p className="text-xs text-slate-400">Total Interest</p>
              <p className="font-mono-data font-bold text-blue-400" data-testid="emi-interest">
                {inrShort(totalInterest)}
              </p>
            </div>
            <div className="rounded-xl bg-slate-950 border border-slate-800 p-3 text-center">
              <p className="text-xs text-slate-400">Total Payable</p>
              <p className="font-mono-data font-bold text-orange-400" data-testid="emi-payable">
                {inrShort(totalPayable)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
